import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, User, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { INPUT_CLS, ERROR_CLS } from '@/components/brand/BrandKit';
import { AuthShell, AuthLink, Field, PasswordField, GoogleButton, OrDivider } from '@/components/account/AuthShell';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { accountRegister, apiError, BRAND_ACCOUNT } from '@/api/account';
import { UaePassButton } from '@/components/account/UaePassButton';

const EMPTY = { name: '', email: '', mobile: '', password: '', company: '', trade_license: '', job_title: '' };
const TYPES = [
  { id: 'retail', label: 'Retail', sub: 'Personal (B2C)', Icon: User },
  { id: 'corporate', label: 'Corporate', sub: 'Business (B2B)', Icon: Building2 },
];

function TypeToggle({ type, setType }) {
  return (
    <div data-testid="register-type" role="tablist" className="grid grid-cols-2 gap-2 rounded-xl bg-[#F5F5F3] p-1.5">
      {TYPES.map(({ id, label, sub, Icon }) => (
        <button key={id} type="button" role="tab" aria-selected={type === id} data-testid={`register-type-${id}`} onClick={() => setType(id)}
          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${type === id ? 'bg-white shadow-sm text-[#121212]' : 'text-[#666666] hover:text-[#121212]'}`}>
          <span className={`grid h-9 w-9 place-items-center rounded-md ${type === id ? 'bg-[#EE5A01] text-white' : 'bg-white text-[#999]'}`}><Icon className="w-4 h-4" /></span>
          <span><span className="block font-heading font-bold text-sm">{label}</span><span className="block font-body text-[11px] text-[#888]">{sub}</span></span>
        </button>
      ))}
    </div>
  );
}

function PendingState({ email }) {
  return (
    <div data-testid="register-pending" className="text-center py-4">
      <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#FFF4ED] text-[#EE5A01]"><Clock className="w-8 h-8" /></span>
      <h2 className="mt-5 font-heading font-black text-2xl text-[#121212]">Request received</h2>
      <p className="mt-2 font-body text-sm text-[#666666]">Thank you. Corporate accounts are verified by our team before activation. We will email <span className="font-bold text-[#121212]">{email}</span> as soon as your account is approved, usually within one business day.</p>
      <p className="mt-6 font-body text-sm text-[#666666]">Need it sooner? Call <a href="tel:800364" className="font-heading font-bold text-[#EE5A01]">800 364</a>.</p>
    </div>
  );
}

function CorporateFields({ form, set }) {
  return (
    <>
      <Field label="Company name" htmlFor="register-company">
        <Input id="register-company" data-testid="register-company" value={form.company} onChange={set('company')} placeholder="Company LLC" autoComplete="organization" className={INPUT_CLS} required />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Trade licence no. (optional)" htmlFor="register-trade-license">
          <Input id="register-trade-license" data-testid="register-trade-license" value={form.trade_license} onChange={set('trade_license')} placeholder="e.g. 123456" className={INPUT_CLS} />
        </Field>
        <Field label="Job title (optional)" htmlFor="register-job-title">
          <Input id="register-job-title" data-testid="register-job-title" value={form.job_title} onChange={set('job_title')} placeholder="Fleet Manager" autoComplete="organization-title" className={INPUT_CLS} />
        </Field>
      </div>
    </>
  );
}

export default function RegisterPage() {
  const { customer, ready, setCustomer } = useCustomerAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get('type') === 'corporate' ? 'corporate' : 'retail');
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState('');

  useEffect(() => { document.title = `Create account | ${BRAND_ACCOUNT}`; }, []);

  if (ready && customer) return <Navigate to="/account" replace />;

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const corporate = type === 'corporate';
  const canSubmit = form.name && form.email && form.mobile && form.password && (!corporate || form.company);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await accountRegister({ ...form, account_type: type });
      if (res.status === 'pending') {
        setPending(res.email);
        return;
      }
      setCustomer(res);
      navigate('/account', { replace: true, state: { isNew: true } });
    } catch (err) {
      setError(apiError(err, 'Could not create your account. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testid="register-page" eyebrow="Create account" title={pending ? 'Corporate account requested' : `Create your ${BRAND_ACCOUNT} account`}
      lead={pending ? '' : 'Link your account to Gmail, authenticate with UAE PASS, or set up an email and password. Personal accounts are active immediately; corporate accounts are verified by our team first.'}
      footer={<>Already have an account? <AuthLink to="/login" testid="register-to-login">Sign in</AuthLink></>}>
      {pending ? <PendingState email={pending} /> : (
        <>
          <TypeToggle type={type} setType={setType} />
          <div className="mt-5 space-y-3">
            <UaePassButton testid="register-uaepass-btn" label="Sign up with UAE PASS" intent="login" accountType={type} />
            {!corporate && <GoogleButton testid="register-google-btn" label="Sign up with Google" />}
          </div>
          <OrDivider />
          <form onSubmit={submit} className="space-y-5" noValidate>
            {error && <p data-testid="register-error" role="alert" className={ERROR_CLS}>{error}</p>}
            <Field label="Full name" htmlFor="register-name">
              <Input id="register-name" data-testid="register-name" value={form.name} onChange={set('name')} placeholder="Your full name" autoComplete="name" className={INPUT_CLS} required />
            </Field>
            {corporate && <CorporateFields form={form} set={set} />}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Email" htmlFor="register-email">
                <Input id="register-email" data-testid="register-email" type="email" value={form.email} onChange={set('email')} placeholder={corporate ? 'you@company.com' : 'you@example.com'} autoComplete="email" className={INPUT_CLS} required />
              </Field>
              <Field label="Mobile number" htmlFor="register-mobile">
                <Input id="register-mobile" data-testid="register-mobile" type="tel" value={form.mobile} onChange={set('mobile')} placeholder="+971 50 123 4567" autoComplete="tel" className={INPUT_CLS} dir="ltr" required />
              </Field>
            </div>
            <Field label="Password" htmlFor="register-password" hint="At least 8 characters, with a letter and a number.">
              <PasswordField id="register-password" testid="register-password" value={form.password} onChange={set('password')} placeholder="Create a password" autoComplete="new-password" />
            </Field>
            <button type="submit" data-testid="register-submit" disabled={loading || !canSubmit} className="btn-primary w-full disabled:opacity-50">
              {loading ? 'Creating account…' : corporate ? 'Request corporate account' : 'Create account'}
            </button>
            <p className="font-body text-[11px] text-[#888] text-center">By continuing you agree to be contacted by Eurogulf Mobility Group about your account and bookings.</p>
          </form>
        </>
      )}
    </AuthShell>
  );
}
